import { Dealership } from "@Types/dealerships";
import { InfoWindow, Map } from "@vis.gl/react-google-maps";
import { Feature, Point } from "geojson";
import { useEffect, useMemo, useState } from "react";

import { Deal } from "@Types/deal";

import {
  DealInfoWindowContent,
  DealershipInfoWindowContent,
} from "./InfoWindows";
import { ClusteredMarkers } from "./Markers/ClusteredMarkers";
import { FeatureMarkerDealership } from "./Markers/DealerMarker";
import {
  DealershipsFeaturesProps,
  DealershipsGeojson,
  DealsFeaturesProps,
  DealsGeojson,
} from "./types";

const hasValidCoordinates = (lat: unknown, lng: unknown): boolean =>
  typeof lat === "number" &&
  typeof lng === "number" &&
  !isNaN(Number(lat)) &&
  !isNaN(Number(lng)) &&
  Number(lat) !== 0 &&
  Number(lng) !== 0;

const filterDealsByDate = (
  deals: Deal[],
  filterType: "all" | "monthly" | "yearly",
  selectedMonth: number,
  selectedYear: number,
  setCenter: React.Dispatch<
    React.SetStateAction<{
      lat: number;
      lng: number;
    }>
  >
) => {
  const filteredDeals = deals.filter((deal) => {
    if (filterType === "all") return true;
    const fundedAt = deal?.data?.info?.dealDates?.fundedAt;

    if (!fundedAt) return false;

    const dealDate = new Date(fundedAt);
    if (filterType === "monthly")
      return (
        dealDate.getMonth() === selectedMonth &&
        dealDate.getFullYear() === selectedYear
      );
    else return dealDate.getFullYear() === selectedYear;
  });

  setCenter({
    lat:
      (filteredDeals ?? [])?.reduce(
        (a, b) => a + Number(b?.data?.applicant?.data?.info?.coordinates?.lat),
        0
      ) / (filteredDeals?.length || 1) || 39.8283,
    lng:
      (filteredDeals ?? [])?.reduce(
        (a, b) => a + Number(b?.data?.applicant?.data?.info?.coordinates?.lon),
        0
      ) / (filteredDeals?.length || 1) || -98.5795,
  });
  return filteredDeals;
};

const filterDealersById = (
  dealers: Dealership[],
  selectedDealerId: string | null
) => {
  if (!selectedDealerId) return dealers;
  return dealers.filter(
    (dealer) =>
      dealer._id === selectedDealerId &&
      hasValidCoordinates(
        dealer?.data?.info?.coordinates?.lat,
        dealer?.data?.info?.coordinates?.lon
      )
  );
};

export default function DataMap({
  deals,
  filterType,
  dealers,
  selectedDealerId,
  selectedMonth,
  selectedYear,
}: {
  deals: Deal[];
  filterType: "monthly" | "yearly" | "all";
  selectedMonth: number;
  selectedYear: number;
  dealers: Dealership[];
  selectedDealerId: string | null;
}) {
  const [zoom, setZoom] = useState(5);
  const [center, setCenter] = useState({
    lat: 39.8283,
    lng: -98.5795,
  });

  const [dealGeoJSON, setDealGeoJSON] = useState<
    Record<"funded", DealsGeojson> | undefined
  >();
  const [dealerGeoJSON, setDealerGeoJSON] = useState<
    { dealership: string; geojson: DealershipsGeojson }[] | undefined
  >();

  const [dealInfowindowData, setDealInfowindowData] = useState<{
    anchor: google.maps.marker.AdvancedMarkerElement;
    features: Feature<Point>[];
  } | null>(null);

  const [dealershipInfoWindowData, setDealershipInfoWindowData] = useState<{
    anchor: google.maps.marker.AdvancedMarkerElement;
    feature: Feature<Point>;
  } | null>(null);

  const handleDealInfoWindowClose = () => setDealInfowindowData(null);
  const handleDealershipInfoWindowClose = () =>
    setDealershipInfoWindowData(null);

  const filteredDealerships = useMemo(
    () => filterDealersById(dealers, selectedDealerId),
    [dealers, selectedDealerId]
  );

  const filteredDeals = useMemo(
    () =>
      filterDealsByDate(
        deals,
        filterType,
        selectedMonth,
        selectedYear,
        setCenter
      ),
    [deals, filterType, selectedMonth, selectedYear]
  );

  useEffect(() => {
    const statusByDealershipGeojsons = filteredDeals
      .filter((deal) =>
        hasValidCoordinates(
          deal.data.applicant.data.info.coordinates?.lat,
          deal.data.applicant.data.info.coordinates?.lon
        )
      )
      .reduce((acc, deal) => {
        const feature: Feature<Point, DealsFeaturesProps> = {
          type: "Feature",
          id: deal._id,
          geometry: {
            type: "Point",
            coordinates: [
              Number(deal?.data?.applicant?.data?.info?.coordinates?.lon),
              Number(deal?.data?.applicant?.data?.info?.coordinates?.lat),
            ],
          },
          properties: {
            applicantNames: `${deal?.data?.applicant?.data?.info?.firstName} ${deal?.data?.applicant?.data?.info?.lastName}`,
            dealershipName: deal?.data?.dealership?.data?.info?.name,
            amountFinanced: deal?.data?.info?.payment?.dealTotal || 0,
            vehicle: {
              year: deal?.data?.info?.vehicle?.year as number,
              make: deal?.data?.info?.vehicle?.make,
              model: deal?.data?.info?.vehicle?.model,
            },
          },
        };

        acc["funded"] = {
          type: "FeatureCollection",
          features: [...(acc?.["funded"]?.features ?? []), feature],
        };

        return acc;
      }, {} as Record<string, DealsGeojson>);
    setDealGeoJSON(statusByDealershipGeojsons);
  }, [filteredDeals.length]);

  useEffect(() => {
    if (filteredDealerships && filteredDealerships?.length) {
      const dealershipGeojsons = filterDealersById(
        filteredDealerships,
        selectedDealerId
      ).map((dealership) => {
        const feature: Feature<Point, DealershipsFeaturesProps> = {
          type: "Feature",
          id: dealership._id,
          geometry: {
            type: "Point",
            coordinates: [
              Number(dealership?.data?.info?.coordinates?.lon),
              Number(dealership?.data?.info?.coordinates?.lat),
            ],
          },
          properties: {
            name:
              dealership?.data?.info?.displayName ||
              dealership?.data?.info?.name,
          },
        };
        return {
          dealership:
            dealership?.data?.info?.displayName || dealership?.data?.info?.name,
          geojson: {
            type: "FeatureCollection" as const,
            features: [feature],
          },
        };
      });

      setDealerGeoJSON(dealershipGeojsons);
    }
  }, [filteredDealerships]);

  return (
    <div
      style={{
        minWidth: 500,
        border: "3px solid #f3f3f3",
        padding: 20,
        marginTop: 10,
        borderRadius: 5,
      }}
    >
        <Map
          id="map"
          style={{ height: 500 }}
          defaultZoom={zoom}
          mapId="49ae42fed52588c3"
          mapTypeId="roadmap"
          onCenterChanged={(center) =>
            setCenter(center.detail.center ?? { lat: 39.8283, lng: -98.5795 })
          }
          onZoomChanged={(zoomEvent) => setZoom(zoomEvent.detail.zoom)}
          defaultCenter={center}
          gestureHandling={"greedy"}
          disableDefaultUI
        >
          {dealGeoJSON &&
            Object.entries(dealGeoJSON).map(([status, geojson], index) => (
              <ClusteredMarkers
                key={`${status}-${index}`}
                clusterColor={"#00ff00"}
                geojson={geojson}
                clusterClickCallback={(infowindowData) => {
                  console.log(infowindowData, "infowindowData");
                  setDealInfowindowData(null);
                  setTimeout(() => {
                    setDealInfowindowData(infowindowData);
                  }, 10);
                }}
                markerClickCallback={(infowindowData) => {
                  console.log(infowindowData, "infowindowData");
                  setDealInfowindowData(null);
                  setTimeout(() => {
                    setDealInfowindowData(infowindowData);
                  }, 10);
                }}
              />
            ))}

          {dealerGeoJSON?.map((dealershipGeojsonData, index) => {
            const dealershipFeature = dealershipGeojsonData.geojson.features[0];

            const [lng, lat] = dealershipFeature.geometry.coordinates;

            if (!hasValidCoordinates(lat, lng)) return null;

            return (
              <FeatureMarkerDealership
                key={`${dealershipGeojsonData.dealership}-${index}-${dealershipFeature.id}`}
                featureId={dealershipFeature.id as string}
                position={{ lat, lng }}
                onMarkerClick={(marker) => {
                  console.log(marker, dealershipFeature, "marker");
                  setDealershipInfoWindowData(null);
                  setTimeout(() => {
                    setDealershipInfoWindowData({
                      anchor: marker,
                      feature: dealershipFeature,
                    });
                  }, 10);
                }}
              />
            );
          })}

          {dealInfowindowData?.anchor && dealInfowindowData.features && (
            <InfoWindow
              style={{ minWidth: "230px" }}
              onCloseClick={handleDealInfoWindowClose}
              anchor={dealInfowindowData.anchor}
            >
              <DealInfoWindowContent features={dealInfowindowData.features} />
            </InfoWindow>
          )}
          {dealershipInfoWindowData && (
            <InfoWindow
              onCloseClick={handleDealershipInfoWindowClose}
              anchor={dealershipInfoWindowData.anchor}
            >
              <DealershipInfoWindowContent
                feature={dealershipInfoWindowData.feature}
              />
            </InfoWindow>
          )}
        </Map>
      <div style={{ display: "flex" }}>
        <div style={{ display: "flex", padding: "10px 20px" }}>
          <div
            style={{
              width: 20,
              height: 20,
              background: "#c800ff",
              display: "inline-block",
              marginRight: 10,
            }}
          ></div>
          Dealers ({filteredDealerships.length})
        </div>

        <div style={{ display: "flex", padding: "10px 20px" }}>
          <div
            style={{
              width: 20,
              height: 20,
              background: "#00ff00",
              display: "inline-block",
              marginRight: 10,
            }}
          ></div>
          Funded loans ({filteredDeals.length})
        </div>
      </div>
    </div>
  );
}
